import { useEffect, useMemo, useState, useCallback } from 'react'
import { AdvancedMarker, AdvancedMarkerAnchorPoint, InfoWindow } from '@vis.gl/react-google-maps'
import { ElementPin } from '../../../lib/mapMarkers'
import { Box, Flex, Text, VStack, Checkbox } from '@chakra-ui/react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  LuChevronLeft as ChevronLeft,
  LuList as List,
  LuFootprints as Footprints,
  LuGauge as Gauge,
  LuTriangleAlert as TriangleAlert,
  LuPause as Pause,
} from 'react-icons/lu'
import type { ActiveTrackingShipmentItem, TrackingPointItem } from '../../../api/models/response'
import { shipmentsService } from '../../../api/services/shipments.service'
import { brand, themeColors } from '../../../theme'
import { parseUtcDate, formatElapsed } from '../../../lib/dates'
import { formatCarbonKg } from '../../../lib/carbonFootprint'
import { haversineMeters, formatDistance } from '../../../lib/trackAnalytics'
import { KMap, Polyline, FitBounds } from '../../../lib/googleMaps'

type LL = google.maps.LatLngLiteral

const STALE_MINUTES = 15

interface LiveStats {
  minsSinceLast: number
  recentDistanceM: number
  estSpeedKmh: number
  stale: boolean
}

/** Estadísticas en vivo sobre los puntos recientes (sin velocidad propia). */
function liveStatsFor(points: TrackingPointItem[]): LiveStats | null {
  if (points.length === 0) return null
  const last = points[points.length - 1]
  const minsSinceLast = Math.max(0, Math.round((Date.now() - parseUtcDate(last.capturedAt).getTime()) / 60000))

  let recentDistanceM = 0
  for (let i = 1; i < points.length; i++) {
    recentDistanceM += haversineMeters(
      points[i - 1].latitude,
      points[i - 1].longitude,
      points[i].latitude,
      points[i].longitude,
    )
  }

  let estSpeedKmh = 0
  if (points.length >= 2) {
    const a = points[points.length - 2]
    const dt = (parseUtcDate(last.capturedAt).getTime() - parseUtcDate(a.capturedAt).getTime()) / 1000
    if (dt > 0) {
      estSpeedKmh = (haversineMeters(a.latitude, a.longitude, last.latitude, last.longitude) / dt) * 3.6
    }
  }

  return { minsSinceLast, recentDistanceM, estSpeedKmh, stale: minsSinceLast > STALE_MINUTES }
}

const MotionBox = motion.create(Box)

const COLORES_TRASLADOS = [
  '#d49a2a', '#3d6fa8', '#2a8a87', '#c8442f',
  '#8B5CF6', '#06B6D4', '#F97316', '#EC4899',
]

interface ActiveShipmentWithColor {
  item: ActiveTrackingShipmentItem
  color: string
}

/** Tope de marcadores intermedios por ruta: la línea dibuja todos los puntos igual. */
const MAX_POINT_MARKERS = 60

// ── Marcadores HTML (hijos de AdvancedMarker) ───────────────

function EndpointMarker({ color, size = 12 }: { color: string; size?: number }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        background: color,
        border: '2px solid white',
        borderRadius: '50%',
        boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
      }}
    />
  )
}

// Distintivo de pausa: pin ámbar que se monta sobre el camión cuando el conductor
// puso el seguimiento en pausa, para verlo de un vistazo en el mapa del dashboard.
function PauseBadgeMarker() {
  return (
    <div
      style={{
        width: 20,
        height: 20,
        background: '#d49a2a',
        border: '2px solid white',
        borderRadius: '50%',
        boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <svg width="9" height="9" viewBox="0 0 24 24" fill="white">
        <rect x="6" y="5" width="4" height="14" rx="1" />
        <rect x="14" y="5" width="4" height="14" rx="1" />
      </svg>
    </div>
  )
}

// ── Main Component ──────────────────────────────────────────

interface Props {
  /** Alto del mapa. Por defecto ocupa el alto de la ventana (pantalla de Traslados). */
  height?: string
  onCountChange?: (count: number) => void
  onLoadingChange?: (loading: boolean) => void
  refreshKey?: number
}

export default function TrasladosActivosView({ onCountChange, onLoadingChange, refreshKey = 0, height = 'calc(100vh - 250px)' }: Props) {
  const colors = themeColors

  const [shipments, setShipments] = useState<ActiveTrackingShipmentItem[]>([])
  const [loading, setLoading] = useState(true)
  const [panelCollapsed, setPanelCollapsed] = useState(false)
  const [toggledOff, setToggledOff] = useState<Set<string>>(new Set())
  /** Marcador de camión con el popup abierto (click), por id de traslado. */
  const [openPopupId, setOpenPopupId] = useState<string | null>(null)
  /** Punto intermedio bajo el cursor: `${itemId}-${index}`. */
  const [hoveredPoint, setHoveredPoint] = useState<string | null>(null)

  // Self-fetch active tracking data
  useEffect(() => {
    let cancelled = false
    const fetchData = async () => {
      try {
        setLoading(true)
        onLoadingChange?.(true)
        const result = await shipmentsService.getActiveTracking()
        if (!cancelled) {
          setShipments(result.shipments)
          onCountChange?.(result.shipments.length)
        }
      } catch {
        if (!cancelled) {
          setShipments([])
          onCountChange?.(0)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
          onLoadingChange?.(false)
        }
      }
    }
    fetchData()
    return () => { cancelled = true }
  }, [onCountChange, onLoadingChange, refreshKey])

  const items: ActiveShipmentWithColor[] = useMemo(
    () => shipments.map((s, i) => ({
      item: s,
      color: COLORES_TRASLADOS[i % COLORES_TRASLADOS.length],
    })),
    [shipments]
  )

  const visibleIds = useMemo(() => {
    const all = new Set(shipments.map(s => s.id))
    if (toggledOff.size === 0) return all
    return new Set([...all].filter(id => !toggledOff.has(id)))
  }, [shipments, toggledOff])

  const toggleTraslado = useCallback((id: string) => {
    setToggledOff(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const selectAll = useCallback(() => {
    setToggledOff(new Set())
  }, [])

  const selectNone = useCallback(() => {
    setToggledOff(new Set(shipments.map(s => s.id)))
  }, [shipments])

  const visibleItems = useMemo(
    () => items.filter(i => visibleIds.has(i.item.id)),
    [items, visibleIds]
  )

  // El encuadre se recalcula cuando cambia QUÉ traslados se ven, no con cada refresco: atado
  // a los puntos, el mapa saltaba cada vez que un camión en viaje reportaba posición.
  const visibleKey = visibleItems.map(v => v.item.id).sort().join('|')
  const boundsPoints = useMemo<LL[]>(() => {
    const pts: LL[] = []
    visibleItems.forEach(({ item }) => {
      item.recentPoints.forEach(p => pts.push({ lat: p.latitude, lng: p.longitude }))
    })
    return pts
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al cambiar los traslados visibles
  }, [visibleKey])
  const boundsNonce = visibleItems.length

  const formatTime = (iso: string) => {
    const d = parseUtcDate(iso)
    return d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })
  }

  const formatDateTime = (iso: string) => {
    const d = parseUtcDate(iso)
    return d.toLocaleDateString('es-PE', { day: '2-digit', month: 'short' }) + ' ' +
      d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })
  }

  if (loading && shipments.length === 0) {
    return (
      <Flex height="400px" alignItems="center" justifyContent="center" borderRadius="2xl" border="1px solid" borderColor={colors.border} bg={colors.bgCard}>
        <Text fontSize="sm" color={colors.textMuted}>Cargando traslados activos...</Text>
      </Flex>
    )
  }

  if (shipments.length === 0) {
    return (
      <Flex height="400px" alignItems="center" justifyContent="center" borderRadius="2xl" border="1px solid" borderColor={colors.border} bg={colors.bgCard}>
        <Text fontSize="sm" color={colors.textMuted}>No hay traslados en transito</Text>
      </Flex>
    )
  }

  return (
    <Box position="relative" height={height} transition="height .45s cubic-bezier(.2,.7,.2,1)" borderRadius="2xl" overflow="hidden" border="1px solid" borderColor={colors.border}>
      {/* Map */}
      <KMap defaultCenter={{ lat: -14.0, lng: -74.0 }} defaultZoom={6} mapTypeId="hybrid" disableDefaultUI>
        <FitBounds points={boundsPoints} padding={60} maxZoom={12} nonce={boundsNonce} />

        {visibleItems.map(({ item, color }) => {
          if (item.recentPoints.length === 0) return null
          const points = item.recentPoints
          const positions: LL[] = points.map(p => ({ lat: p.latitude, lng: p.longitude }))
          // Un viaje largo (Trujillo–Lima ≈ 2.500 puntos) creaba un marcador HTML por punto y
          // congelaba el navegador. La línea conserva TODOS los puntos; los marcadores con
          // detalle al pasar el cursor se reparten a lo largo del trayecto (como máximo
          // MAX_POINT_MARKERS) conservando su número de punto real.
          const middle = points.slice(1, -1).map((p, i) => ({ p, n: i + 2 }))
          const stride = Math.max(1, Math.ceil(middle.length / MAX_POINT_MARKERS))
          const sampled = stride === 1 ? middle : middle.filter((_, i) => i % stride === 0)
          const lastPoint = points[points.length - 1]
          const live = liveStatsFor(points)

          return (
            <span key={item.id}>
              {/* Polyline */}
              <Polyline path={positions} strokeColor={color} strokeWeight={3} strokeOpacity={0.85} />

              {/* GPS point markers (intermediate) */}
              {sampled.map(({ p, n }) => {
                const ptKey = `${item.id}-${n}`
                return (
                  <AdvancedMarker
                    key={`pt-${ptKey}`}
                    position={{ lat: p.latitude, lng: p.longitude }}
                    anchorPoint={AdvancedMarkerAnchorPoint.CENTER}
                    onMouseEnter={() => setHoveredPoint(ptKey)}
                    onMouseLeave={() => setHoveredPoint((cur) => (cur === ptKey ? null : cur))}
                  >
                    <EndpointMarker color={color} size={8} />
                  </AdvancedMarker>
                )
              })}

              {/* GPS point tooltip (hover) */}
              {sampled.map(({ p, n }) => {
                const ptKey = `${item.id}-${n}`
                if (hoveredPoint !== ptKey) return null
                return (
                  <InfoWindow
                    key={`tip-${ptKey}`}
                    position={{ lat: p.latitude, lng: p.longitude }}
                    pixelOffset={[0, -6]}
                    disableAutoPan
                    headerDisabled
                  >
                    <Box fontFamily="'Instrument Sans', sans-serif" p={1}>
                      <Text fontSize="10px" fontWeight="bold" color="#1a1a1a">
                        Punto {n} de {points.length}
                      </Text>
                      <Text fontSize="10px" color="#555">
                        {formatDateTime(p.capturedAt)}
                      </Text>
                    </Box>
                  </InfoWindow>
                )
              })}

              {/* Origin marker */}
              <AdvancedMarker
                position={{ lat: points[0].latitude, lng: points[0].longitude }}
                onMouseEnter={() => setHoveredPoint(`${item.id}-origin`)}
                onMouseLeave={() => setHoveredPoint((cur) => (cur === `${item.id}-origin` ? null : cur))}
              >
                <ElementPin kind="origin" size={30} />
              </AdvancedMarker>
              {hoveredPoint === `${item.id}-origin` && (
                <InfoWindow
                  position={{ lat: points[0].latitude, lng: points[0].longitude }}
                  pixelOffset={[0, -34]}
                  disableAutoPan
                  headerDisabled
                >
                  <Box fontFamily="'Instrument Sans', sans-serif" p={1}>
                    <Text fontSize="10px" fontWeight="bold" color="#2a8a87">Origen</Text>
                    <Text fontSize="10px" color="#555">{formatDateTime(points[0].capturedAt)}</Text>
                  </Box>
                </InfoWindow>
              )}

              {/* Truck marker (last known position) */}
              <AdvancedMarker
                position={{ lat: lastPoint.latitude, lng: lastPoint.longitude }}
                onClick={() => setOpenPopupId((cur) => (cur === item.id ? null : item.id))}
              >
                <ElementPin kind="truck" color={color} />
              </AdvancedMarker>

              {/* Distintivo de pausa montado sobre el camión */}
              {item.isPaused && (
                <AdvancedMarker
                  position={{
                    lat: item.pauseLatitude ?? lastPoint.latitude,
                    lng: item.pauseLongitude ?? lastPoint.longitude,
                  }}
                  anchorPoint={AdvancedMarkerAnchorPoint.BOTTOM_CENTER}
                  zIndex={20}
                  onClick={() => setOpenPopupId((cur) => (cur === item.id ? null : item.id))}
                >
                  <div style={{ marginBottom: 42 }}>
                    <PauseBadgeMarker />
                  </div>
                </AdvancedMarker>
              )}
              {openPopupId === item.id && (
                <InfoWindow
                  position={{ lat: lastPoint.latitude, lng: lastPoint.longitude }}
                  pixelOffset={[0, -44]}
                  onCloseClick={() => setOpenPopupId(null)}
                >
                  <Box fontFamily="'Instrument Sans', sans-serif" lineHeight="1.4">
                    <Flex alignItems="center" gap={2} mb={1}>
                      <Box width="8px" height="8px" borderRadius="full" bg={color} flexShrink={0} />
                      <Text fontWeight="bold" fontSize="xs" color="#1a1a1a">
                        {item.batchCode}
                      </Text>
                    </Flex>
                    <Text fontSize="xs" fontWeight="600" color="#333">
                      {item.driverName || '-'}
                    </Text>
                    <Text fontSize="11px" color="#555">
                      {item.vehiclePlate || '-'} · {item.mineralTypeName || '-'} · {item.mineralWeight || '-'} {item.measurementUnitName || ''}
                    </Text>
                    <Text fontSize="11px" color="#555" maxW="250px">
                      {item.supplierName || '-'} → {item.collectionCenterName || '-'}
                    </Text>
                    {item.isPaused && (
                      <Flex alignItems="center" gap={1} mt={1}>
                        <Pause size={12} color="#8a5f0b" fill="#8a5f0b" />
                        <Text fontSize="11px" fontWeight="700" color="#8a5f0b">
                          EN PAUSA{item.pauseReason ? ` · ${item.pauseReason}` : ''}
                        </Text>
                      </Flex>
                    )}
                    <Flex alignItems="center" gap={1} mt={1}>
                      <Footprints size={12} color={item.carbonKg != null ? '#1d6664' : '#999'} />
                      <Text fontSize="11px" fontWeight="600" color={item.carbonKg != null ? '#1d6664' : '#999'}>
                        {formatCarbonKg(item.carbonKg)}
                      </Text>
                    </Flex>
                    {live && (
                      <Flex alignItems="center" gap={1} mt={0.5} flexWrap="wrap">
                        <Gauge size={12} color="#3d6fa8" />
                        <Text fontSize="11px" fontWeight="600" color="#333">
                          ~{live.estSpeedKmh.toFixed(0)} km/h
                        </Text>
                        <Text fontSize="11px" color="#777">
                          · {formatDistance(live.recentDistanceM)} recientes
                        </Text>
                      </Flex>
                    )}
                    <Flex alignItems="center" gap={1} mt={0.5}>
                      {live?.stale && <TriangleAlert size={11} color="#EAB308" />}
                      <Text fontSize="10px" color={live?.stale ? '#8a5f0b' : '#999'}>
                        Último reporte: {formatTime(lastPoint.capturedAt)}
                        {live && live.minsSinceLast > 0 && ` (hace ${formatElapsed(live.minsSinceLast)})`}
                      </Text>
                    </Flex>
                  </Box>
                </InfoWindow>
              )}
            </span>
          )
        })}
      </KMap>

      {/* Floating Panel */}
      <AnimatePresence mode="wait">
        {!panelCollapsed ? (
          <MotionBox
            key="panel"
            position="absolute"
            top={4}
            left={4}
            zIndex={1000}
            width="320px"
            maxHeight="80%"
            bg="white"
            borderRadius="2xl"
            border="1px solid"
            borderColor={colors.border}
            overflow="hidden"
            display="flex"
            flexDirection="column"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            {/* Panel Header */}
            <Flex p={4} pb={3} justifyContent="space-between" alignItems="center" borderBottom="1px solid" borderColor={colors.border}>
              <Box>
                <Text fontSize="sm" fontWeight="bold" color={colors.text}>
                  Vehiculos en Transito
                </Text>
                <Text fontSize="10px" color={colors.textMuted}>
                  {visibleIds.size} de {shipments.length} visibles
                </Text>
              </Box>
              <Box
                as="button"
                p={1.5}
                borderRadius="md"
                cursor="pointer"
                color={colors.textMuted}
                _hover={{ bg: colors.bgHover, color: colors.text }}
                transition="all 0.2s"
                onClick={() => setPanelCollapsed(true)}
              >
                <ChevronLeft size={18} />
              </Box>
            </Flex>

            {/* Quick Actions */}
            <Flex px={4} py={2} gap={3} borderBottom="1px solid" borderColor={colors.border}>
              <Text
                as="button"
                fontSize="xs"
                fontWeight="600"
                color={brand.green}
                cursor="pointer"
                _hover={{ textDecoration: 'underline' }}
                onClick={selectAll}
              >
                Todos
              </Text>
              <Text
                as="button"
                fontSize="xs"
                fontWeight="600"
                color={colors.textMuted}
                cursor="pointer"
                _hover={{ textDecoration: 'underline', color: colors.text }}
                onClick={selectNone}
              >
                Ninguno
              </Text>
            </Flex>

            {/* Traslados List */}
            <VStack gap={0} alignItems="stretch" overflowY="auto" flex="1" px={2} py={2}>
              {items.map(({ item, color }) => {
                const live = liveStatsFor(item.recentPoints)
                return (
                <Flex
                  key={item.id}
                  alignItems="center"
                  gap={3}
                  px={2}
                  py={2.5}
                  borderRadius="xl"
                  cursor="pointer"
                  _hover={{ bg: colors.bgHover }}
                  transition="all 0.15s"
                  onClick={() => toggleTraslado(item.id)}
                >
                  <Box onClick={e => e.stopPropagation()}>
                    <Checkbox.Root
                      size="sm"
                      checked={visibleIds.has(item.id)}
                      onCheckedChange={() => toggleTraslado(item.id)}
                    >
                      <Checkbox.HiddenInput />
                      <Checkbox.Control>
                        <Checkbox.Indicator />
                      </Checkbox.Control>
                    </Checkbox.Root>
                  </Box>
                  <Box width="10px" height="10px" borderRadius="full" bg={color} flexShrink={0} />
                  <Box flex="1" minW={0}>
                    <Text fontSize="xs" fontWeight="bold" color={colors.text} truncate>
                      {item.vehiclePlate || '-'}
                    </Text>
                    <Text fontSize="10px" color={colors.textMuted} truncate>
                      {item.supplierName || '-'} → {item.collectionCenterName || '-'}
                    </Text>
                    <Text fontSize="10px" color={colors.textDimmed}>
                      {item.mineralTypeName || '-'} · {item.mineralWeight || '-'} {item.measurementUnitName || ''}
                    </Text>
                    <Flex alignItems="center" gap={2} mt={0.5} flexWrap="wrap">
                      <Flex alignItems="center" gap={1}>
                        <Footprints size={11} color={item.carbonKg != null ? brand.green : colors.textDimmed} />
                        <Text fontSize="10px" fontWeight="600" color={item.carbonKg != null ? brand.green : colors.textDimmed}>
                          {formatCarbonKg(item.carbonKg)}
                        </Text>
                      </Flex>
                      {live && (
                        <Flex alignItems="center" gap={1}>
                          <Gauge size={11} color="#3d6fa8" />
                          <Text fontSize="10px" fontWeight="600" color={colors.textSecondary}>
                            ~{live.estSpeedKmh.toFixed(0)} km/h
                          </Text>
                        </Flex>
                      )}
                    </Flex>
                    {item.isPaused && (
                      <Flex alignItems="center" gap={1} mt={0.5}>
                        <Pause size={11} color="#8a5f0b" fill="#8a5f0b" />
                        <Text fontSize="10px" fontWeight="700" color="#8a5f0b" truncate>
                          En pausa{item.pauseReason ? ` · ${item.pauseReason}` : ''}
                        </Text>
                      </Flex>
                    )}
                    {live?.stale && (
                      <Flex alignItems="center" gap={1} mt={0.5}>
                        <TriangleAlert size={11} color={brand.gold} />
                        <Text fontSize="10px" fontWeight="600" color={brand.goldDark}>
                          Sin reporte hace {formatElapsed(live.minsSinceLast)}
                        </Text>
                      </Flex>
                    )}
                  </Box>
                </Flex>
                )
              })}
            </VStack>
          </MotionBox>
        ) : (
          <MotionBox
            key="collapsed"
            position="absolute"
            top={4}
            left={4}
            zIndex={1000}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
          >
            <Flex
              as="button"
              alignItems="center"
              gap={2}
              px={3}
              py={2.5}
              bg="white"
              borderRadius="2xl"
              border="1px solid"
              borderColor={colors.border}
              cursor="pointer"
              _hover={{ borderColor: brand.green }}
              transition="all 0.2s"
              onClick={() => setPanelCollapsed(false)}
            >
              <List size={18} color={colors.text} />
              <Box
                bg={brand.green}
                color="white"
                px={2}
                py={0.5}
                borderRadius="full"
                fontSize="10px"
                fontWeight="bold"
              >
                {visibleIds.size}
              </Box>
            </Flex>
          </MotionBox>
        )}
      </AnimatePresence>
    </Box>
  )
}
